/* @flow */
import { observable, computed, flow, toJS, action, when, reaction } from 'mobx';

import { ApiModel } from 'api/ApiModel';
import type { WidgetItem } from 'stores/DashboardStore';
import { Roles } from 'permissions/defs';
import { GLOBAL_FALLBACK_LANG, getI18nInstance } from 'i18nInstance';

import {
  errorHandlerService,
  authenticationStore,
  widgetStore,
  dashboardStore,
} from '../services/ServiceRegistry';

export const FALLBACK_LANGUAGE_KEY = 'fallbackLanguage';
export const UNSET_LANGUAGE_CODE = 'un';

export default class UserStore extends ApiModel {
  constructor(api: *) {
    super(api);

    this.getUserLanguages();

    when(
      () => authenticationStore.authenticationState === 'authed',
      () => {
        this.getActiveUser();

        if (!this.userLanguages && !this.loadingUserLanguages) {
          this.getUserLanguages();
        }
      }
    );

    reaction(
      () => this.userLanguage,
      async userLanguage => {
        const i18nInstance = await getI18nInstance();

        if (userLanguage && i18nInstance.language !== userLanguage) {
          console.debug('Changing language to:', userLanguage);
          i18nInstance.changeLanguage(userLanguage);
        }

        if (userLanguage) {
          localStorage.setItem(FALLBACK_LANGUAGE_KEY, userLanguage);
        }
      },
      { fireImmediately: true }
    );
  }

  @observable savingUserProfile = false;

  @observable errorSaveingProfile: ?string = null;

  @observable loadingUserActivity = true;
  @observable loadingUserLanguages = false;

  @observable userActivity: ?Array<*> = null;

  @observable userLocation: ?Array<*> = null;

  @observable userStates: ?Array<*> = null;

  @observable userLanguagesUnfiltered: ?Array<*> = null;
  @observable forwardedLanguage: ?string = null;

  @observable userCurrencies: ?Array<*> = null;

  @observable userPriceFormats: ?Array<*> = null;

  @observable themUserId: string = '';

  @observable userType: ?string = null;

  @computed get userRoleId(): number {
    if (this.activeUser) {
      return this.activeUser.role_id;
    }

    return Roles.LOGIN;
  }
  /**
   * @deprecated in favour of `userRoleId`
   */
  @computed get userRoleName(): ?string {
    if (this.activeUser) {
      return this.activeUser.role_name;
    }

    return 'Login';
  }

  @computed get isReadOnly(): boolean {
    if (this.activeUser) {
      return this.activeUser.scope === 'read-only';
    }

    return true;
  }

  @computed get userLanguage() {
    if (
      (this.userLoaded && this.userLanguages) ||
      authenticationStore.authenticationState === 'unauthed'
    ) {
      if (
        this.activeUser &&
        this.activeUser.language &&
        this.activeUser.language.code !== UNSET_LANGUAGE_CODE
      ) {
        return this.activeUser.language.code;
      }
    }

    if (this.forwardedLanguage) {
      return this.forwardedLanguage;
    }

    const localStorageLanguage = localStorage.getItem(FALLBACK_LANGUAGE_KEY);
    if (
      localStorageLanguage &&
      localStorageLanguage !== UNSET_LANGUAGE_CODE &&
      localStorageLanguage !== 'null'
    ) {
      return localStorageLanguage;
    }

    return GLOBAL_FALLBACK_LANG;
  }

  @computed get userLanguages() {
    return (this.userLanguagesUnfiltered || []).filter(
      language => language.code !== UNSET_LANGUAGE_CODE
    );
  }

  @observable activeUser = null;

  @observable selectedUser = null;

  @observable selectedUserImpersonate = null;

  @computed get userLoaded(): boolean {
    return !this.reloadingUser && !!this.activeUser;
  }
  @observable reloadingUser = false;

  performActiveUserSideEffects = (
    updateIds: { favouriteWidgets: string, widgets: string },
    favouriteWidgetsOverride?: string[]
  ) => {
    if (dashboardStore.widgetConfigLoaded) {
      widgetStore._optimisticFavouriteWidgets.updateFromRequested(
        favouriteWidgetsOverride || this.activeUser.favourite_widgets,
        updateIds.favouriteWidgets
      );
    }

    const newWidgetPreferences = this.activeUser.widget_preferences.map(
      widgetPreferenceToWidgetItem()
    );
    // Yield for the migration from local storage in the Dashboard Store
    if (newWidgetPreferences.length) {
      widgetStore._optimisticWidgets.updateFromRequested(newWidgetPreferences, updateIds.widgets);
    }
  };

  getActiveUser = flow(function*(updateIds?: { favouriteWidgets?: string, widgets?: string }): any {
    this.reloadingUser = true;

    yield when(() => authenticationStore.authenticationState === 'authed');

    let _updateIds = updateIds;
    if (!_updateIds) {
      _updateIds = {};
    }
    if (!_updateIds.favouriteWidgets) {
      _updateIds.favouriteWidgets = widgetStore._optimisticFavouriteWidgets.markRequested();
    }
    if (!_updateIds.widgets) {
      _updateIds.widgets = widgetStore._optimisticWidgets.markRequested();
    }

    try {
      const user = yield this.api.getResource('users/active');

      let returnUser = false;

      if (authenticationStore.authenticationState === 'authed') {
        // Only save the user if we have not cancelled the previous auth sequence
        this.saveUserToLocal(user);
        this.setActiveUserFromLocal();

        let favouriteWidgetsOverride: ?(string[]);
        if (!dashboardStore.widgetConfigLoaded) {
          const { favouriteWidgets } = dashboardStore.loadWidgetConfig();

          favouriteWidgetsOverride = favouriteWidgets;
        }

        this.performActiveUserSideEffects(_updateIds, favouriteWidgetsOverride);
        returnUser = true;
      }
      this.reloadingUser = false;
      return returnUser ? user : null;
    } catch (error) {
      this.reloadingUser = false;
      throw error;
    }
  });

  getSelectedUser = flow(function*(userId: string): any {
    try {
      const user: Object = yield this.api.getResource(`users/${userId}`);
      this.setSelectedUser(user);
      return user;
    } catch (error) {
      throw error;
    }
  });

  @action
  setThemUserId(userId: string) {
    this.themUserId = userId;
  }

  @action
  setUserType(userType: string) {
    this.userType = userType;
  }

  saveUserToLocal(user: Object) {
    if (typeof Storage !== 'undefined') {
      if (user) {
        const localUser: Object = JSON.parse(localStorage.getItem('user'));

        // Keep read only mode after get active user refresh
        // NOTE: Keeping this here for legacy references (there shouldn't be any, but who knows)
        if ((localUser && localUser.read_only) || user.scope === 'read-only') {
          user.read_only = true; // eslint-disable-line camelcase
        }

        localStorage.setItem('user', JSON.stringify(user));
      } else {
        localStorage.setItem(
          'user',
          JSON.stringify({
            role_id: Roles.LOGIN, // eslint-disable-line
          })
        );
      }
    }
  }

  @action
  getActiveUserFromLocal(): Object {
    try {
      const user: * = localStorage.getItem('user');

      if (user) {
        return JSON.parse(user);
      }

      return {
        role_id: Roles.LOGIN,
      };
    } catch (err) {
      errorHandlerService.handleException(err, 'UserStore.getActiveUserFromLocal');
      return {
        role_id: Roles.LOGIN,
      };
    }
  }

  @action
  updateSelectedUser(userDetails: Object) {
    const { selectedUser } = this;
    const mergedUser = Object.assign(selectedUser, userDetails);
    this.setSelectedUser(mergedUser);
  }

  @action
  updateActiveUser(userDetails: Object) {
    const activeUser = this.getActiveUserFromLocal();
    const mergedUser = Object.assign(activeUser, userDetails);
    this.saveUserToLocal(mergedUser);
    this.setActiveUserFromLocal();
  }

  @action
  setActiveUserFromLocal() {
    const user: * = localStorage.getItem('user');
    this.activeUser = JSON.parse(user);
  }

  @action
  setSelectedUser(user: Object) {
    this.selectedUser = user;
  }

  @action
  clearSelectedUser() {
    this.themUserId = '';
    this.selectedUser = null;
  }

  removeUserFromLocal() {
    if (typeof Storage !== 'undefined') {
      localStorage.removeItem('user');
    }
  }

  async loadUserActivityById(id: string): Promise<*> {
    this.loadingUserActivity = true;

    try {
      const res: ?Array<*> = await this.api.getResource('users/' + id + '/activity');
      this.userActivity = res;
    } catch (e) {
      errorHandlerService.handleException(e, 'UserStore.loadUserActivityById');
    }

    this.loadingUserActivity = false;
  }

  async getUserLocationState(id: string): Promise<*> {
    try {
      const data: Object = await this.api.getResource('countries/' + id + '/states');
      this.userStates = data;
    } catch (e) {
      errorHandlerService.handleException(e, 'UserStore.getUserLocationState');
    }
  }

  async getUserLocations(): Promise<*> {
    try {
      const data: Object = await this.api.getResource('countries/list');
      this.userLocation = data;
    } catch (e) {
      errorHandlerService.handleException(e, 'UserStore.getUserLocations');
    }
  }

  locationStateModel = (): Promise<*> => {
    return new Promise((resolve: Function) => {
      resolve(toJS(this.userStates));
    });
  };

  locationCountryModel = (): Promise<*> => {
    return new Promise((resolve: Function) => {
      resolve(toJS(this.userLocation));
    });
  };

  @action
  async getUserLanguages() {
    this.loadingUserLanguages = true;

    try {
      const data = await this.api.oAuthStore.getJsonGuest('languages', { body: undefined });
      this.userLanguagesUnfiltered = data;

      this.loadingUserLanguages = false;
      return data;
    } catch (e) {
      errorHandlerService.handleException(e, 'UserStore.getUserLanguages');
    }

    this.loadingUserLanguages = false;
    return undefined;
  }

  languagesModel = async () => toJS(this.userLanguages);

  async getUserCurrencies(): Promise<*> {
    try {
      const data: Object = await this.api.getResource('currencies');
      this.userCurrencies = data;
    } catch (e) {
      errorHandlerService.handleException(e, 'UserStore.getUserCurrencies');
    }
  }

  currenciesModel = (): Promise<*> => {
    return new Promise((resolve: Function) => {
      resolve(toJS(this.userCurrencies));
    });
  };

  async getUserPriceFormats(): Promise<*> {
    try {
      const data: Object = await this.api.getResource('price-formats');
      this.userPriceFormats = data;
    } catch (e) {
      errorHandlerService.handleException(e, 'UserStore.getUserPriceFormats');
    }
  }

  priceFormatsModel = (): Promise<*> => {
    return new Promise((resolve: Function) => {
      resolve(toJS(this.userPriceFormats));
    });
  };

  @action
  updateUserLanguage = async (newLanguageCode: string) => {
    if (this.activeUser) {
      const targetLanguage = this.userLanguagesUnfiltered.find(
        language => language.code === newLanguageCode
      );

      if (targetLanguage && this.activeUser) {
        await this.api.putResource(`users/profile/${this.activeUser.id}`, {
          first_name: this.activeUser.first_name,
          last_name: this.activeUser.last_name,
          tel: this.activeUser.tel,
          country_id: this.activeUser.country_id,
          state: this.activeUser.state,
          company_name: this.activeUser.company_name,
          email: this.activeUser.email,
          language_id: targetLanguage.id,
        });

        return this.getActiveUser();
      }
    }

    this.forwardedLanguage = newLanguageCode;
  };

  @action
  clearUser = () => {
    authenticationStore.logout();
    this.removeUserFromLocal();
    this.activeUser = null;
  };
}

export const widgetPreferenceToWidgetItem = (includeId?: boolean) => (
  widgetPreference: any
): WidgetItem | (WidgetItem & { id: string }) => ({
  ...(includeId ? { id: widgetPreference.id } : {}),
  widget: widgetPreference.name,
  position: widgetPreference.position,
  variant: widgetPreference.variant,
});
